import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useStore } from "@/contexts/StoreContext";
import ProductCard from "@/components/customer/ProductCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SlidersHorizontal, X, PackageSearch } from "lucide-react";
import { toast } from "sonner";

// Same palette used by CategoryBrowse for continuity.
const gradientFor = (name = "") => {
  const palettes = [
    ["#FFE5D9", "#FFB68C"], ["#E6F4E5", "#8BD581"], ["#E5EBFF", "#8AA7FF"],
    ["#FFE9F3", "#FF9EC7"], ["#FFF3D1", "#FFC24A"], ["#E8F7F5", "#4CC7B5"],
    ["#F1E3FF", "#B87CFF"], ["#FFE1D5", "#FF7A4A"],
  ];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  const [a, b] = palettes[h % palettes.length];
  return `linear-gradient(135deg, ${a}, ${b})`;
};

export default function Shop() {
  const { user } = useAuth();
  const { categoryFont } = useStore();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const categoryId = params.get("category") || "";
  const featured = params.get("featured") === "1";

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(24);
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState([]);
  const [autoThumb, setAutoThumb] = useState({});

  // Sprint 28 — admin-controlled category label font
  const fontCls =
    categoryFont === "display"   ? "font-display italic" :
    categoryFont === "semibold"  ? "font-bold" :
    categoryFont === "condensed" ? "font-semibold tracking-tighter" :
    /* default */                  "font-medium";

  useEffect(() => {
    if (!user) return;
    api.get("/categories?limit=100")
      .then((r) => {
        // Sprint 22: hide TEST_* seed data + inactive categories from customer chips.
        const clean = (r.data.items || []).filter(
          (c) => c.is_active !== false && !/^TEST[_-]/i.test(c.name || "") && !/^TEST[_-]/i.test(c.slug || "")
        );
        setCats(clean);
      })
      .catch(() => {});
  }, [user]);

  // Parent categories only for the Blinkit-style left rail (Blinkit does not
  // mix parent + sub in the rail — subs live inside the CategoryBrowse detail
  // page, which this Shop page cross-links to on tap).
  const railCats = useMemo(() => cats.filter((c) => !c.parent_id), [cats]);

  // Auto-thumbnail: fill missing category images with the first product's image.
  useEffect(() => {
    if (!user || railCats.length === 0) return;
    const needy = railCats.filter((c) => !c.image_url && autoThumb[c.id] === undefined);
    if (needy.length === 0) return;
    let cancelled = false;
    (async () => {
      const pairs = await Promise.all(
        needy.slice(0, 30).map(async (c) => {
          try {
            const { data } = await api.get(`/products?category_id=${c.id}&limit=1`);
            const first = (data.items || []).find((x) => x.thumbnail_url);
            return [c.id, first?.thumbnail_url || null];
          } catch { return [c.id, null]; }
        })
      );
      if (cancelled) return;
      const next = {};
      for (const [id, url] of pairs) next[id] = url || "";
      setAutoThumb((prev) => ({ ...prev, ...next }));
    })();
    return () => { cancelled = true; };
  }, [railCats, user, autoThumb]);

  const load = useCallback(async (pg = page) => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page: pg, limit });
      if (q) qs.set("q", q);
      if (categoryId) qs.set("category_id", categoryId);
      if (featured) qs.set("featured", "true");
      const { data } = await api.get(`/products?${qs}`);
      setRows((data.items || []).filter((p) => !/^TEST[_-]/i.test(p.name || "")));
      setTotal(data.total || 0);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [q, categoryId, featured, page, limit, user]);

  useEffect(() => { load(page); }, [load, page]);
  useEffect(() => { setPage(1); }, [q, categoryId, featured]);

  const activeCat = useMemo(() => cats.find((c) => c.id === categoryId), [cats, categoryId]);

  const updateParams = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "" || v === false) next.delete(k);
      else next.set(k, v);
    });
    setParams(next, { replace: true });
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-16 text-center" data-testid="shop-signin-cta">
        <div className="w-16 h-16 rounded-2xl bg-accent/10 text-accent grid place-items-center mx-auto mb-4">
          <SlidersHorizontal className="w-7 h-7" />
        </div>
        <h1 className="text-3xl font-display font-semibold">Sign in to browse the aisles</h1>
        <p className="mt-3 text-muted-foreground">Live prices, live stock, personalised recommendations — sign in first.</p>
        <Button asChild size="lg" className="mt-6 btn-primary-glow">
          <Link to="/login?redirect=/shop">Sign in / create account</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-0 md:px-3 py-2 md:py-4" data-testid="shop-page">
      {/* Sprint 58 — Search moved to header (single source of truth). Compact title strip. */}
      {(activeCat || q || featured) && (
        <div className="mb-2 px-3 md:px-2">
          <p className="text-xs md:text-sm text-muted-foreground">
            {activeCat ? <>Browsing <span className="text-foreground font-semibold">{activeCat.name}</span></> : q ? <>Results for "<span className="text-foreground font-semibold">{q}</span>"</> : "Featured products"}
          </p>
        </div>
      )}

      {/* Blinkit-style two-column layout: slim left rail + right products grid */}
      <div className={`grid gap-0 ${railCats.length > 0 ? "grid-cols-[74px_1fr] md:grid-cols-[96px_1fr]" : "grid-cols-1"}`}>
        {railCats.length > 0 && (
          <aside
            className="bg-secondary/30 md:rounded-l-xl border-r border-border/50 md:border md:border-r-0 sticky top-[76px] md:top-24 h-[calc(100vh-4.5rem)] md:h-[calc(100vh-7rem)] self-start overflow-y-auto no-scrollbar"
            data-testid="shop-cat-rail"
          >
            {/* "All" pill */}
            <button
              onClick={() => updateParams({ category: "" })}
              className={`w-full flex flex-col items-center gap-0.5 px-1 py-2 transition relative ${
                !categoryId ? "bg-white text-primary font-semibold" : "hover:bg-white/60 text-foreground/70"
              }`}
              data-testid="shop-cat-all"
            >
              {!categoryId && (
                <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-primary rounded-r" />
              )}
              <div className="w-[54px] h-[54px] md:w-[60px] md:h-[60px] rounded-2xl grid place-items-center shadow-[0_2px_6px_-2px_rgba(0,0,0,0.08)] bg-gradient-to-br from-primary/20 to-primary/5">
                <SlidersHorizontal className="w-6 h-6 md:w-7 md:h-7 text-primary" />
              </div>
              <div className={`text-[10px] leading-[1.15] text-center px-0.5 ${fontCls}`}>All</div>
            </button>

            {railCats.map((c, i) => {
              const active = c.id === categoryId;
              const thumb = c.image_url || autoThumb[c.id];
              return (
                <button
                  key={c.id}
                  onClick={() => updateParams({ category: c.id })}
                  className={`w-full flex flex-col items-center gap-0.5 px-1 py-2 transition relative ${
                    active ? "bg-white text-primary font-semibold" : "hover:bg-white/60 text-foreground/70"
                  }`}
                  data-testid={`shop-cat-${i}`}
                >
                  {active && (
                    <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-primary rounded-r" />
                  )}
                  <div
                    className={`w-[54px] h-[54px] md:w-[60px] md:h-[60px] rounded-xl grid place-items-center overflow-hidden shadow-[0_2px_6px_-2px_rgba(0,0,0,0.08)] ${active ? "ring-1 ring-primary/60" : ""}`}
                    style={{ background: gradientFor(c.name || "") }}
                  >
                    {thumb
                      ? <img src={thumb} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
                      : <span className="text-xl font-bold text-white/90 drop-shadow-sm">{(c.name || "?").slice(0, 1).toUpperCase()}</span>}
                  </div>
                  <div className={`text-[10px] leading-[1.15] text-center line-clamp-2 px-0.5 ${fontCls}`}>
                    {c.name}
                  </div>
                </button>
              );
            })}
          </aside>
        )}

        <section
          className={`min-w-0 bg-card ${railCats.length > 0 ? "md:rounded-r-xl border md:border-l-0" : "rounded-xl border"} border-border/60 px-2 md:px-4 py-2 md:py-3`}
          data-testid="shop-products"
        >
          {(q || categoryId || featured) && (
            <div className="mb-2 flex items-center gap-2">
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setParams(new URLSearchParams(), { replace: true })} data-testid="clear-filters">
                <X className="w-3 h-3 mr-1" /> Clear
              </Button>
              <span className="text-xs text-muted-foreground">
                {total} product{total === 1 ? "" : "s"}
              </span>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5 md:gap-3" data-testid="shop-grid">
            {loading && Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="card-soft border-transparent overflow-hidden">
                <Skeleton className="aspect-square w-full" />
                <div className="p-3 space-y-2"><Skeleton className="h-4 w-3/4" /><Skeleton className="h-3 w-1/3" /></div>
              </div>
            ))}
            {!loading && rows.length === 0 && (
              <div className="col-span-full py-16 text-center" data-testid="shop-empty">
                <div className="w-16 h-16 rounded-2xl bg-secondary text-primary/60 grid place-items-center mx-auto mb-3">
                  <PackageSearch className="w-7 h-7" />
                </div>
                <div className="font-display text-xl font-semibold">No products match your filters</div>
                <div className="text-sm text-muted-foreground mt-1 mb-4">Try clearing filters or search a different keyword.</div>
                <Button variant="outline" onClick={() => setParams(new URLSearchParams(), { replace: true })} data-testid="shop-empty-clear">
                  Clear filters
                </Button>
              </div>
            )}
            {!loading && rows.map((p) => (
              <ProductCard key={p.id} p={p} />
            ))}
          </div>

          {total > limit && (
            <div className="flex items-center justify-center gap-2 mt-6" data-testid="shop-pager">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
              <div className="text-xs text-muted-foreground">Page {page} / {totalPages}</div>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
